import { useEffect, useState, type ReactNode } from 'react';
import { useRouterState } from '@tanstack/react-router';
import { AsideHeader, FooterItem, MobileHeader, type AsideHeaderItem } from '@gravity-ui/navigation';
import type { MobileMenuItem } from '@gravity-ui/navigation';
import { Button, Icon, Text, type IconData } from '@gravity-ui/uikit';
import {
  ArrowRightFromSquare,
  ClockArrowRotateLeft,
  FolderHouse,
  House,
  ListTimeline,
  Person,
  Persons,
  Receipt,
} from '@gravity-ui/icons';
import type { AdminMeResponse } from '@odonix/contracts';
import { useSessionStore } from '../lib/session/context.js';
import { isPlainClick, useGo } from '../ui/router-link.js';

/**
 * Постоянная часть экрана — боковая панель консоли: разделы слева, внизу —
 * кто вошёл и выход. Панель сворачивается до значков, и это помнится: на
 * ноутбуке 1366 px таблице клиник нужна каждая колонка.
 *
 * Без индикатора обмена и переключателя клиники: эта оболочка не хранит кэш,
 * не работает без сети и не принадлежит ни одной клинике.
 */

interface SectionDef {
  id: string;
  to: string;
  title: string;
  icon: IconData;
  matches: (path: string) => boolean;
}

const SECTIONS: readonly SectionDef[] = [
  { id: 'home', to: '/', title: 'Обзор', icon: House, matches: (path) => path === '/' },
  { id: 'tenants', to: '/tenants', title: 'Клиники', icon: FolderHouse, matches: (path) => path.startsWith('/tenants') },
  {
    id: 'managers',
    to: '/managers',
    title: 'Менеджеры',
    icon: Persons,
    matches: (path) => path.startsWith('/managers') && !path.startsWith('/managers/audit'),
  },
  { id: 'expenses', to: '/expenses', title: 'Расходы', icon: Receipt, matches: (path) => path.startsWith('/expenses') },
  { id: 'audit', to: '/audit', title: 'Журнал действий', icon: ListTimeline, matches: (path) => path.startsWith('/audit') },
  {
    id: 'managers-audit',
    to: '/managers/audit',
    title: 'Действия менеджеров',
    icon: ClockArrowRotateLeft,
    matches: (path) => path.startsWith('/managers/audit'),
  },
];

const COMPACT_KEY = 'odonix.admin.aside.compact';

function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() => window.matchMedia(query).matches);
  useEffect(() => {
    const list = window.matchMedia(query);
    const update = () => {
      setMatches(list.matches);
    };
    list.addEventListener('change', update);
    return () => {
      list.removeEventListener('change', update);
    };
  }, [query]);
  return matches;
}

function useCompact(): [boolean, (value: boolean) => void] {
  const [compact, setCompact] = useState(() => window.localStorage.getItem(COMPACT_KEY) === '1');
  return [
    compact,
    (value) => {
      setCompact(value);
      window.localStorage.setItem(COMPACT_KEY, value ? '1' : '0');
    },
  ];
}

export function AppShell({ staff, children }: { staff: AdminMeResponse; children: ReactNode }) {
  const store = useSessionStore();
  const go = useGo();
  const path = useRouterState({ select: (state) => state.location.pathname });
  const phone = useMediaQuery('(max-width: 767px)');
  const [compact, setCompact] = useCompact();

  const logo = {
    text: 'Odonix Admin',
    iconSrc: '/icons/icon-64.png',
    iconSize: 28,
    href: '/',
    'aria-label': 'Odonix Admin, к обзору',
    onClick: (event: React.MouseEvent<HTMLElement>) => {
      if (!isPlainClick(event)) return;
      event.preventDefault();
      go('/');
    },
  };

  const content = () => <main className="min-h-full bg-[var(--g-color-base-background)]">{children}</main>;

  if (phone) {
    const items: MobileMenuItem[] = SECTIONS.map((section) => ({
      id: section.id,
      title: section.title,
      icon: section.icon,
      current: section.matches(path),
      onItemClick: () => {
        go(section.to);
      },
    }));
    return (
      <MobileHeader
        logo={logo}
        burgerMenu={{
          items,
          renderFooter: () => (
            <div className="flex flex-col items-start gap-2 p-4">
              <Text color="secondary">{staff.fullName ?? staff.email}</Text>
              <Button view="outlined" onClick={() => void store.logout()}>
                <Icon data={ArrowRightFromSquare} size={16} />
                Выйти
              </Button>
            </div>
          ),
        }}
        burgerOpenTitle="Разделы"
        burgerCloseTitle="Закрыть"
        renderContent={content}
      />
    );
  }

  const menuItems: AsideHeaderItem[] = SECTIONS.map((section) => ({
    id: section.id,
    title: section.title,
    icon: section.icon,
    href: section.to,
    current: section.matches(path),
    onItemClick: (_item, _collapsed, event) => {
      if (!isPlainClick(event)) return;
      event.preventDefault();
      go(section.to);
    },
  }));

  return (
    <AsideHeader
      logo={logo}
      compact={compact}
      onChangeCompact={setCompact}
      headerDecoration
      menuItems={menuItems}
      collapseTitle="Свернуть панель"
      expandTitle="Развернуть панель"
      renderContent={content}
      renderFooter={() => (
        <>
          <FooterItem id="staff" icon={Person} title={staff.fullName ?? staff.email} tooltipText={staff.email} />
          <FooterItem
            id="logout"
            icon={ArrowRightFromSquare}
            title="Выйти"
            onItemClick={() => void store.logout()}
          />
        </>
      )}
    />
  );
}
